import { generateChangelogSlug, slugifySegment } from "./articles"

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const MAX_PORTAL_SLUG_LENGTH = 200
export const MAX_DOCS_URL_NAME_SEGMENTS = 3

export type SlugResult =
	| { ok: true; value: string }
	| { ok: false; message: string }

export const isUuidSlug = (slug: string) => UUID.test(slug)

const hasWordCharacter = (segment: string) => /[\p{L}\p{N}]/u.test(segment)

function stripPath(input: string, section: "docs" | "changelog"): string {
	let value = input.trim()
	try {
		if (/^https?:\/\//i.test(value)) value = new URL(value).pathname
	} catch {
		// Not a URL, keep the raw text.
	}
	value = value.replace(/^\/+|\/+$/g, "")
	const prefix = `${section}/`
	if (value.startsWith(prefix)) value = value.slice(prefix.length)
	return value
}

export function normalizeDocsUrlName(input: string): SlugResult {
	const value = stripPath(input, "docs")
	if (!value) {
		return { ok: false, message: "Enter a URL like guides/getting-started." }
	}
	const segments = value.split("/").filter((s) => s.trim().length > 0)
	if (segments.length > MAX_DOCS_URL_NAME_SEGMENTS) {
		return {
			ok: false,
			message: `A docs URL has at most ${MAX_DOCS_URL_NAME_SEGMENTS} segments.`,
		}
	}
	if (segments.some((s) => !hasWordCharacter(s) || !slugifySegment(s))) {
		return {
			ok: false,
			message: "Use letters, numbers, and hyphens, with / between segments.",
		}
	}
	const normalized = segments.map((s) => slugifySegment(s)).join("/")
	if (normalized.length > MAX_PORTAL_SLUG_LENGTH) {
		return {
			ok: false,
			message: `A URL can be at most ${MAX_PORTAL_SLUG_LENGTH} characters.`,
		}
	}
	if (isUuidSlug(normalized)) {
		return { ok: false, message: "A URL cannot look like an article id." }
	}
	return { ok: true, value: normalized }
}

export function normalizeChangelogSlug(input: string): SlugResult {
	const value = stripPath(input, "changelog")
	if (!value) {
		return { ok: false, message: "Enter a URL like 2026-09-08-faster-exports." }
	}
	if (value.includes("/")) {
		return { ok: false, message: "A changelog URL is one segment without /." }
	}
	if (!hasWordCharacter(value)) {
		return { ok: false, message: "Use letters, numbers, and hyphens." }
	}
	const normalized = slugifySegment(value)
	if (normalized.length > MAX_PORTAL_SLUG_LENGTH) {
		return {
			ok: false,
			message: `A URL can be at most ${MAX_PORTAL_SLUG_LENGTH} characters.`,
		}
	}
	if (isUuidSlug(normalized)) {
		return { ok: false, message: "A URL cannot look like a changelog id." }
	}
	return { ok: true, value: normalized }
}

// The slug an article would get from its title today. Matches the server's
// buildArticleUrlName before any collision suffix.
export function deriveDocsUrlName(
	groupName: string | null | undefined,
	title: string,
): string {
	const group = slugifySegment(groupName)
	const article = slugifySegment(title)
	return group ? `${group}/${article}` : article
}

type DateLike = Date | number | string

const toDate = (value: DateLike | null | undefined): Date | null =>
	value == null ? null : value instanceof Date ? value : new Date(value)

// A stored slug wins; rows that predate the column keep deriving from the date.
export function getChangelogSlug(
	row: {
		slug?: string | null
		date?: DateLike | null
		title: string
		createdAt?: DateLike | null
	},
	timezone: string | null | undefined,
): string {
	if (row.slug) return row.slug
	return generateChangelogSlug(
		toDate(row.date),
		row.title,
		toDate(row.createdAt) ?? undefined,
		timezone ?? "Europe/Berlin",
	)
}
